import React from 'react'

interface TrendChartProps {
  title: string
  data: Array<{
    date: string
    value: number
  }>
  height?: number
  color?: string
}

export const TrendChart: React.FC<TrendChartProps> = ({
  title,
  data,
  height = 200,
  color = 'rgb(147, 51, 234)'
}) => {
  if (data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
        <div className="flex items-center justify-center h-48 text-gray-500">
          暂无数据
        </div>
      </div>
    )
  }

  const values = data.map(d => {
    const v = Number(d.value)
    return Number.isFinite(v) ? v : 0
  })
  const maxValue = Math.max(...values)
  const minValue = Math.min(...values)
  const range = maxValue - minValue
  const safeRange = range === 0 ? 1 : range

  // 格式化日期
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return `${date.getMonth() + 1}/${date.getDate()}`
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      <div className="relative" style={{ height: `${height}px` }}>
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 400 200"
          preserveAspectRatio="none"
          className="w-full h-full"
        >
          {/* 网格线 */}
          {[0, 1, 2, 3, 4].map((i) => (
            <line
              key={`grid-${i}`}
              x1="40"
              y1={i * 40 + 20}
              x2="380"
              y2={i * 40 + 20}
              stroke="#e5e7eb"
              strokeWidth="1"
            />
          ))}

          {/* 数据线 */}
          <polyline
            points={data.map((point, index) => {
              const x = 40 + (index * (340 / Math.max(data.length - 1, 1)))
              const value = values[index]
              const y = 180 - ((value - minValue) / safeRange) * 140
              const cy = Number.isFinite(y) ? y : 180
              return `${x},${cy}`
            }).join(' ')}
            fill="none"
            stroke={color}
            strokeWidth="2"
          />

          {/* 数据点 */}
          {data.map((point, index) => {
            const x = 40 + (index * (340 / Math.max(data.length - 1, 1)))
            const value = values[index]
            const y = 180 - ((value - minValue) / safeRange) * 140
            const cy = Number.isFinite(y) ? y : 180
            return (
              <circle
                key={`point-${index}`}
                cx={x}
                cy={cy}
                r="4"
                fill={color}
                className="hover:r-6"
              />
            )
          })}

          {/* X轴标签 */}
          {data.map((point, index) => {
            const x = 40 + (index * (340 / Math.max(data.length - 1, 1)))
            return (
              <text
                key={`label-${index}`}
                x={x}
                y="195"
                textAnchor="middle"
                className="text-xs fill-gray-600"
              >
                {(() => {
                  try {
                    return formatDate(point.date)
                  } catch {
                    return `${index + 1}`
                  }
                })()}
              </text>
            )
          })}
        </svg>
      </div>

      {/* 图例 */}
      <div className="mt-4 flex items-center justify-center space-x-4">
        <div className="flex items-center">
          <div
            className="w-3 h-3 rounded-full mr-2"
            style={{ backgroundColor: color }}
          />
          <span className="text-sm text-gray-600">数值</span>
        </div>
      </div>
    </div>
  )
}
